import { useWorkOrder } from '@/api/equipment/work-orders/getWorkOrder';
import { Spinner } from '@/components/Elements';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/elements/sheet';
import { WorkOrderEditSheetBody } from '@/features/equipment/components/work-orders/WorkOrderEditSheet';
import { ModelID, WorkOrder } from '@/types';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

type WorkOrderSheetContextData = {
  openWorkOrder: (workOrderId: ModelID) => void;
};

type WorkOrderSheetProviderProps = {
  children: ReactNode;
};

export const WorkOrderSheetContext = createContext({} as WorkOrderSheetContextData);

export const useWorkOrderSheet = () => useContext(WorkOrderSheetContext);

export const WorkOrderSheetProvider = ({ children }: WorkOrderSheetProviderProps) => {
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<ModelID | null>(null);
  const [fetchWorkOrderId, setFetchWorkOrderId] = useState<ModelID | null>(null);
  const [loadedWorkOrder, setLoadedWorkOrder] = useState<WorkOrder | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const { data, isError } = useWorkOrder({
    id: fetchWorkOrderId ?? 0,
    config: {
      enabled: fetchWorkOrderId !== null,
      throwOnError: false,
    },
  });

  const selectedWorkOrder = data as WorkOrder | undefined;

  useEffect(() => {
    if (isOpen && selectedWorkOrderId !== null && fetchWorkOrderId !== selectedWorkOrderId) {
      setFetchWorkOrderId(selectedWorkOrderId);
    }
  }, [fetchWorkOrderId, isOpen, selectedWorkOrderId]);

  useEffect(() => {
    if (isOpen && selectedWorkOrder) {
      setLoadedWorkOrder(selectedWorkOrder);
    }
  }, [isOpen, selectedWorkOrder]);

  const openWorkOrder = (workOrderId: ModelID) => {
    setSelectedWorkOrderId(workOrderId);
    setFetchWorkOrderId(null);
    setLoadedWorkOrder(null);
    setIsOpen(true);
  };

  const closeWorkOrder = () => {
    setIsOpen(false);
  };

  const clearClosedWorkOrder = () => {
    if (!isOpen) {
      setSelectedWorkOrderId(null);
      setFetchWorkOrderId(null);
      setLoadedWorkOrder(null);
    }
  };

  return (
    <WorkOrderSheetContext.Provider value={{ openWorkOrder }}>
      {children}
      {selectedWorkOrderId !== null ? (
        <Sheet open={isOpen} onOpenChange={(nextOpen) => !nextOpen && closeWorkOrder()}>
          <SheetContent
            onCloseAutoFocus={clearClosedWorkOrder}
            side="right"
            className="w-full sm:max-w-md overflow-y-auto"
          >
            {loadedWorkOrder ? (
              <WorkOrderEditSheetBody
                key={loadedWorkOrder.task_id}
                workOrder={loadedWorkOrder}
                onClose={closeWorkOrder}
              />
            ) : (
              <>
                <SheetHeader>
                  <SheetTitle>
                    {isError ? 'Unable to load work order' : 'Loading work order'}
                  </SheetTitle>
                  <SheetDescription>
                    {isError
                      ? 'The work order could not be loaded. Close the sheet and try again.'
                      : 'Fetching the latest work order details from the server.'}
                  </SheetDescription>
                </SheetHeader>
                <div className="flex min-h-40 items-center justify-center">
                  {isError ? (
                    <p className="text-sm text-muted-foreground">Unable to load work order.</p>
                  ) : (
                    <Spinner size="md" />
                  )}
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      ) : null}
    </WorkOrderSheetContext.Provider>
  );
};
